import type { JsonObject } from "@arti/dashboard-events";

import { useMemo } from "react";

import type { UsageSummarySnapshot } from "../types";

import {
  type ProviderProgressEntry,
  useProviderProgress,
} from "./useProviderProgress";

export interface RealtimeTaskUsage {
  /** Total tokens (input + output) */
  totalTokens: number;
  /** Input/prompt tokens */
  inputTokens: number;
  /** Output/completion tokens */
  outputTokens: number;
  /** Cached input tokens */
  cachedTokens: number;
  /** Reasoning tokens (for models that support it) */
  reasoningTokens: number;
  /** Estimated cost in USD (null if unavailable) */
  estimatedCostUsd: number | null;
  /** True if data is from real-time events, false if from final summary */
  isRealtime: boolean;
  /** True if we have any usage data */
  hasData: boolean;
}

interface UsageUpdateEventData {
  totalUsage?: {
    inputTokens?: number;
    outputTokens?: number;
    cachedTokens?: number;
    reasoningTokens?: number;
  };
  estimatedCost?: number | null;
}

function parseUsageUpdateEventData(value: JsonObject): UsageUpdateEventData {
  return value as UsageUpdateEventData;
}

export function deriveRealtimeTaskUsage(
  events: ProviderProgressEntry[],
  usageSummary: UsageSummarySnapshot | null | undefined,
  isTaskRunning: boolean,
): RealtimeTaskUsage {
  // If task is complete and we have a final summary, use that
  if (!isTaskRunning && usageSummary?.totals) {
    const { totals } = usageSummary;
    return {
      cachedTokens: totals.cachedInputTokens,
      estimatedCostUsd: totals.costUsd ?? null,
      hasData: true,
      inputTokens: totals.promptTokens,
      isRealtime: false,
      outputTokens: totals.completionTokens,
      reasoningTokens: 0, // Not tracked in final summary
      totalTokens: totals.totalTokens,
    };
  }

  // Aggregate from real-time events
  // Find the latest usage:update event per stream and sum them
  const latestByStream = new Map<string, UsageUpdateEventData>();

  for (const event of events) {
    if (event.event_type === "usage:update") {
      const data = parseUsageUpdateEventData(event.event_data);
      // Always use the latest event for each stream (events are chronological)
      latestByStream.set(event.stream_id, data);
    }
  }

  if (latestByStream.size === 0) {
    return {
      cachedTokens: 0,
      estimatedCostUsd: null,
      hasData: false,
      inputTokens: 0,
      isRealtime: true,
      outputTokens: 0,
      reasoningTokens: 0,
      totalTokens: 0,
    };
  }

  // Sum across all streams
  let inputTokens = 0;
  let outputTokens = 0;
  let cachedTokens = 0;
  let reasoningTokens = 0;
  let estimatedCostUsd: number | null = null;

  for (const data of latestByStream.values()) {
    const usage = data.totalUsage;
    if (usage) {
      inputTokens += usage.inputTokens ?? 0;
      outputTokens += usage.outputTokens ?? 0;
      cachedTokens += usage.cachedTokens ?? 0;
      reasoningTokens += usage.reasoningTokens ?? 0;
    }
    if (typeof data.estimatedCost === "number") {
      estimatedCostUsd = (estimatedCostUsd ?? 0) + data.estimatedCost;
    }
  }

  return {
    cachedTokens,
    estimatedCostUsd,
    hasData: true,
    inputTokens,
    isRealtime: true,
    outputTokens,
    reasoningTokens,
    totalTokens: inputTokens + outputTokens,
  };
}

/**
 * Hook to get real-time task usage by aggregating provider progress events.
 * Falls back to the final usage summary when the task is complete.
 *
 * @param taskId - The task ID to get usage for
 * @param usageSummary - The final usage summary (available after task completion)
 * @param isTaskRunning - Whether the task is currently running
 */
export function useRealtimeTaskUsage(
  taskId: string,
  usageSummary: UsageSummarySnapshot | null | undefined,
  isTaskRunning: boolean,
): RealtimeTaskUsage {
  const { getEventsForTask } = useProviderProgress();
  const events = getEventsForTask(taskId);

  return useMemo(
    () => deriveRealtimeTaskUsage(events, usageSummary, isTaskRunning),
    [events, usageSummary, isTaskRunning],
  );
}
